import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getCampaignsApi,
  sendCampaignApi,
  duplicateCampaignApi,
  retryCampaignApi,
  cancelCampaignApi,
} from '../api/campaigns.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';

const STATUS_CONFIG = {
  sent:         { label: 'Sent',         cls: 'badge-sent',      icon: 'fa-check' },
  scheduled:    { label: 'Scheduled',    cls: 'badge-scheduled', icon: 'fa-calendar' },
  draft:        { label: 'Draft',        cls: 'badge-draft',     icon: 'fa-pen' },
  needs_review: { label: 'Needs review', cls: 'badge-review',    icon: 'fa-eye' },
  cancelled:    { label: 'Cancelled',    cls: 'badge-cancelled', icon: 'fa-ban' },
  failed:       { label: 'Failed',       cls: 'badge-failed',    icon: 'fa-circle-xmark' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, cls: 'badge-draft', icon: 'fa-circle' };
  return (
    <span className={`badge ${cfg.cls}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <i className={`fas ${cfg.icon}`} style={{ fontSize: 9 }} />
      {cfg.label}
    </span>
  );
}

function ChannelIcons({ channel }) {
  const channels = Array.isArray(channel) ? channel : (channel ? [channel] : []);
  const iconMap = { push: 'fa-bell', email: 'fa-envelope', sms: 'fa-comment-sms', in_app: 'fa-mobile-screen' };
  return (
    <div style={{ display: 'flex', gap: 5 }}>
      {channels.map((ch) => (
        <span
          key={ch}
          title={ch}
          style={{ width: 22, height: 22, borderRadius: 6, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <i className={`fas ${iconMap[ch] || 'fa-paper-plane'}`} style={{ fontSize: 9, color: '#074f78' }} />
        </span>
      ))}
    </div>
  );
}

export default function CampaignManager() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [actioning, setActioning] = useState(null); // campaignId being actioned
  const {
    page,
    limit,
    loading,
    setLoading,
    resetPage,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(20);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function loadCampaigns(pageNum = page, overrides = {}) {
    setLoading(true);
    const status = overrides.status !== undefined ? overrides.status : filterStatus;
    const query = overrides.search !== undefined ? overrides.search : search;

    const params = { page: pageNum, limit, status: status || 'all' };
    const result = await apiHandler(() => getCampaignsApi(params));

    if (result.success) {
      const data = unwrapData(result);
      let rows = data?.campaigns || [];
      // Client-side search filter
      if (query.trim()) {
        const q = query.toLowerCase();
        rows = rows.filter(
          (c) =>
            c.campaignName?.toLowerCase().includes(q) ||
            c.segment?.segmentName?.toLowerCase().includes(q) ||
            c.reportingTag?.toLowerCase().includes(q)
        );
      }
      setCampaigns(rows);
      applyPaginationMeta(data);
    } else {
      showToast(result.message || 'Failed to load campaigns', 'error');
    }
    setLoading(false);
  }

  useEffect(() => {
    loadCampaigns(page);
  }, [page, filterStatus]);

  function applyFilters() {
    resetPage();
    loadCampaigns(1);
  }

  function resetFilters() {
    setFilterStatus('all');
    setSearch('');
    resetPage();
    loadCampaigns(1, { status: 'all', search: '' });
  }

  async function handleSend(campaignId) {
    if (!window.confirm('Send this campaign immediately to all segment recipients?')) return;
    setActioning(campaignId);
    const result = await apiHandler(() => sendCampaignApi(campaignId));
    setActioning(null);
    if (result.success) {
      const data = unwrapData(result);
      const recipients = data?.recipientCount ?? 0;
      showToast(`Sent! ${recipients} recipient${recipients !== 1 ? 's' : ''} reached.`, 'success');
      loadCampaigns(page);
    } else {
      showToast(result.message || 'Send failed', 'error');
    }
  }

  async function handleDuplicate(campaignId) {
    setActioning(campaignId);
    const result = await apiHandler(() => duplicateCampaignApi(campaignId));
    setActioning(null);
    if (result.success) {
      const data = unwrapData(result);
      showToast(`Duplicated as "${data?.campaignName}"`, 'success');
      loadCampaigns(page);
    } else {
      showToast(result.message || 'Duplicate failed', 'error');
    }
  }

  async function handleRetry(campaignId) {
    setActioning(campaignId);
    const result = await apiHandler(() => retryCampaignApi(campaignId));
    setActioning(null);
    if (result.success) {
      showToast('Retry queued for failed deliveries', 'success');
      loadCampaigns(page);
    } else {
      // API returns success:false with NO_FAILED_DELIVERIES — show as info
      showToast(result.message || 'No failed deliveries to retry', 'info');
    }
  }

  async function handleCancel(campaignId) {
    if (!window.confirm('Cancel this scheduled campaign?')) return;
    setActioning(campaignId);
    const result = await apiHandler(() => cancelCampaignApi(campaignId));
    setActioning(null);
    if (result.success) {
      showToast('Campaign cancelled', 'success');
      loadCampaigns(page);
    } else {
      showToast(result.message || 'Cancel failed', 'error');
    }
  }

  return (
    <>
      {toast && (
        <div
          style={{
            position: 'fixed', top: 70, right: 20, zIndex: 9999,
            background: toast.type === 'success' ? '#F0FDF4' : toast.type === 'error' ? '#FEF2F2' : '#EFF6FF',
            border: `1px solid ${toast.type === 'success' ? '#b7e5bb' : toast.type === 'error' ? '#FECACA' : '#BFDBFE'}`,
            color: toast.type === 'success' ? '#15803D' : toast.type === 'error' ? '#B91C1C' : '#1D4ED8',
            padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,.08)',
          }}
        >
          <i
            className={`fas ${toast.type === 'success' ? 'fa-circle-check' : toast.type === 'error' ? 'fa-circle-xmark' : 'fa-circle-info'}`}
            style={{ marginRight: 7 }}
          />
          {toast.msg}
        </div>
      )}

      <PageHeader
        title="Campaign Manager"
        icon="fa-comment-dots"
        trail={[{ label: 'Campaign Manager' }]}
        actions={
          <Link to="/message-composer" className="action-btn btn-orange">
            <i className="fas fa-plus" /> New Message
          </Link>
        }
      />

      <div className="section-card">
        <div className="cm-toolbar">
          <div className="cm-toolbar-start">
            <div className="cm-search-wrap">
              <i className="fas fa-magnifying-glass" aria-hidden />
              <input
                type="text"
                className="cm-search-input"
                placeholder="Search campaigns…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              />
            </div>
            <select
              className="cm-filter-select"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); resetPage(); }}
              aria-label="Status"
            >
              <option value="all">All statuses</option>
              <option value="sent">Sent</option>
              <option value="scheduled">Scheduled</option>
              <option value="draft">Draft</option>
              <option value="needs_review">Needs review</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="cm-toolbar-end">
            <button type="button" className="cm-apply-btn" onClick={applyFilters}>
              <i className="fas fa-check" /> Apply
            </button>
            <button type="button" className="cm-reset-link" onClick={resetFilters}>
              Reset filters
            </button>
          </div>
        </div>

        <div className="cm-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Audience</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Sent At</th>
                <th>Reach</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>Loading…</td></tr>
              ) : campaigns.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>No campaigns found</td></tr>
              ) : (
                campaigns.map((c) => {
                  const isActioning = actioning === c.id;
                  return (
                    <tr key={c.id}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#0F172A' }}>{c.campaignName}</div>
                        {c.reportingTag && (
                          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                            <i className="fas fa-tag" style={{ fontSize: 9, marginRight: 3 }} />{c.reportingTag}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: '#475569' }}>
                        {c.segment?.segmentName || '—'}
                        {c.segment?.estimatedUserCount > 0 && (
                          <div style={{ fontSize: 11, color: '#94A3B8' }}>
                            ~{c.segment.estimatedUserCount.toLocaleString()} users
                          </div>
                        )}
                      </td>
                      <td><ChannelIcons channel={c.channel} /></td>
                      <td><StatusBadge status={c.status} /></td>
                      <td style={{ fontSize: 12, color: '#64748B' }}>
                        {c.sentAt ? new Date(c.sentAt).toLocaleString() : c.scheduledAt ? (
                          <span style={{ color: '#1D4ED8' }}>
                            <i className="fas fa-calendar" style={{ fontSize: 9, marginRight: 4 }} />
                            {new Date(c.scheduledAt).toLocaleString()}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {c.recipientCount > 0 ? (
                          <span style={{ fontWeight: 600, color: '#15803D' }}>{c.recipientCount.toLocaleString()}</span>
                        ) : (
                          <span style={{ color: '#94A3B8' }}>—</span>
                        )}
                        {c.openRate > 0 && (
                          <div style={{ fontSize: 11, color: '#64748B' }}>{c.openRate}% open</div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {/* Analytics — available for sent campaigns */}
                          {c.status === 'sent' && (
                            <button
                              type="button"
                              className="ss-icon-btn"
                              title="Analytics"
                              onClick={() => navigate(`/reports?campaignId=${c.id}`)}
                              style={{ color: '#074f78' }}
                            >
                              <i className="fas fa-chart-line" />
                            </button>
                          )}
                          {/* Send Now — draft or scheduled */}
                          {(c.status === 'draft' || c.status === 'scheduled') && (
                            <button
                              type="button"
                              className="ss-icon-btn"
                              title="Send Now"
                              onClick={() => handleSend(c.id)}
                              disabled={isActioning}
                              style={{ color: '#2E7D32' }}
                            >
                              <i className={`fas ${isActioning ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
                            </button>
                          )}
                          {/* Duplicate */}
                          <button
                            type="button"
                            className="ss-icon-btn"
                            title="Duplicate"
                            onClick={() => handleDuplicate(c.id)}
                            disabled={isActioning}
                          >
                            <i className={`fas ${isActioning ? 'fa-spinner fa-spin' : 'fa-copy'}`} />
                          </button>
                          {/* Retry — sent or failed */}
                          {(c.status === 'sent' || c.status === 'failed') && (
                            <button
                              type="button"
                              className="ss-icon-btn"
                              title="Retry failed deliveries"
                              onClick={() => handleRetry(c.id)}
                              disabled={isActioning}
                              style={{ color: '#D9A441' }}
                            >
                              <i className={`fas ${isActioning ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} />
                            </button>
                          )}
                          {/* Cancel — scheduled only */}
                          {c.status === 'scheduled' && (
                            <button
                              type="button"
                              className="ss-icon-btn"
                              title="Cancel schedule"
                              onClick={() => handleCancel(c.id)}
                              disabled={isActioning}
                              style={{ color: '#B91C1C' }}
                            >
                              <i className={`fas ${isActioning ? 'fa-spinner fa-spin' : 'fa-ban'}`} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination {...paginationProps} />
      </div>
    </>
  );
}
